import React, { useRef } from 'react';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import { Navigate } from 'react-router-dom';
import {
  useClaimAllGoodsMutation,
  useGetWorkshopQuery,
} from '@api/queries/workshop';
import { AppRoute } from '@common/app/routes';
import { useCountDownBoostsTimers } from '@common/hooks/count-down-boosts-timers';
import { useDisabledDelay } from '@common/hooks/disabled-delay';
import PageBody from '@components/Products/PageBody/PageBody';
import { MainButton } from '@components/ui/Button/Button';
import { boostWorkshopTimersEnd } from '@redux/selectors/workshopSelectors';
import { useAppSelector } from '@redux/store';

import { useExtendedInfoQuery } from '@/api/queries/user';
import { ToastNotification } from '@/common/components/ToastNotification/ToastNotification';
import { gameItemOrder } from '@/common/constants';
import { useBlurHeader } from '@/common/hooks/blur-header';
import { useCheckStorageOverflow } from '@/common/hooks/check-storage-overflow';
import { playSound } from '@/common/lib/soundEffect';

import stockPng from '/img/tabbar/office.png';
import stockWebp from '/img/tabbar/office.webp';

const Goods: React.FC = () => {
  useExtendedInfoQuery();
  const ref = useRef(null);
  useBlurHeader(ref);
  useGetWorkshopQuery(undefined, { refetchOnMountOrArgChange: true });
  const { t } = useTranslation();
  const workshops = useAppSelector((state) => state.workshop.list);
  const [claimAllGoods, { isLoading }] = useClaimAllGoodsMutation();
  const checkStockOverflow = useCheckStorageOverflow();
  const [disabled, setDisabled] = useDisabledDelay();
  const isTimersEnd = useAppSelector(boostWorkshopTimersEnd);
  useCountDownBoostsTimers(!isTimersEnd, 'Workshop');

  const onClaim = (): void => {
    claimAllGoods()
      .unwrap()
      .then(({ storage }) => {
        checkStockOverflow(storage, gameItemOrder.goods);
        ToastNotification(t('Goods sent to Stock'), {
          img: stockPng,
          webp: stockWebp,
        });
      })
      .catch((error) => {
        console.log(error);
        ToastNotification(
          t('Error: Failed to send resources'),
          undefined,
          'justify-center',
        );
      });
  };

  if (!workshops) {
    return null;
  }

  if (workshops.length === 0) {
    return <Navigate to={AppRoute.WORKSHOP + '/build'} replace={true} />;
  }

  return (
    <main id="workshop-list" className="h-full flex flex-col">
      <Helmet>
        <title>Goods</title>
      </Helmet>
      <div
        ref={ref}
        className="tabs tabs--sticky flex-1 overflow-y-auto hidden-scrollbar"
      >
        <PageBody />
      </div>
      <div className="bg-main-bg w-full p-2">
        <MainButton
          onClick={() => {
            playSound('buttons/switch');
            setDisabled(true);
            onClaim();

            // if (workshops) firebaseLogCollectResourceWorkshop(workshops);
          }}
          inactive={disabled}
          disabled={isLoading || disabled}
          isLoading={isLoading}
          text={t('Send to Stock')}
        />
      </div>
    </main>
  );
};

export default Goods;
